import React, { useMemo } from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { calculateSharePartialDisposalTax } from '../../../../shared/src/calculation/entityDisposalTax.ts';
import { DisposalCgtEstimatePanel } from '../agedCare/DisposalCgtEstimatePanel.tsx';
import {
    ENTITY_STRUCTURE_FUNDING_DISCLAIMER,
    computeRadFundingTotals,
    getEntityEntryFeeAssetGroups,
    getPersonalLiquidAssetsForRad,
    sumEntityRadFunding,
} from '../../utils/entityFundingUtils.ts';
import { isEntityOwnership } from '../../../../shared/src/calculation/constants/clientStructureTypes.ts';
import {
    computeRvEntryFeeFundingTotals,
    computeRvIpNetForAllocation,
} from '../../../../shared/src/calculation/retirementVillageCalculations.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAsset, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { assetDisplayName } from '../../utils/assetDisplayName.ts';

type DraftAsset = FactFindAsset;

/** An asset row as decorated by utils/entityFundingUtils.ts (getPersonalLiquidAssetsForRad /
 *  getEntityEntryFeeAssetGroups) or built locally here for personal investment properties. */
interface AssetRow extends DraftAsset {
    typeId: string;
    typeName: string;
    assetId: string;
    hasCgt?: boolean;
    isInvestmentProperty?: boolean;
}

type FundingMap = Record<string, number>;

interface EntryFeeAssetSliderProps {
    asset: AssetRow;
    saleAmount: number;
    onFundingChange: (assetId: string, value: string | number | undefined) => void;
    netLine?: React.ReactNode;
    /** Already claimed of this asset by the Debt Strategy card — one pool, two claimants. */
    reserved?: number;
    /** For the account's name: a couple's two "MLC"s gain their owners' names (assetDisplayName). */
    factFind?: DraftFactFind;
}

function EntryFeeAssetSlider({ asset, saleAmount, onFundingChange, netLine, reserved = 0, factFind }: EntryFeeAssetSliderProps) {
    const assetValue = parseFloat(String(asset.value)) || 0;
    const available = Math.max(0, assetValue - reserved);
    // Typed amounts are clamped as well as dragged ones — the slider's `max` cannot police the
    // currency box beside it, and an over-allocation here would spend a dollar the debt card has.
    const changeClamped = (value: string | number | undefined) =>
        onFundingChange(asset.assetId, Math.min(Math.max(0, parseFloat(String(value)) || 0), available));

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700">
                {assetDisplayName(asset, factFind, asset.typeName)}
                {asset.typeId === 'shares' && asset.costBase ? (
                    <span className="block text-xs font-normal text-gray-500">
                        Amount to liquidate (CGT deducted before entry fee credited)
                    </span>
                ) : null}
            </label>
            <div className="flex items-center gap-2">
                <input
                    type="range"
                    min="0"
                    max={available}
                    value={saleAmount}
                    onChange={(e) => changeClamped(e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="w-32">
                    <CurrencyInput
                        value={saleAmount || 0}
                        onChange={(v) => changeClamped(v)}
                        placeholder="0"
                        className=""
                    />
                </div>
            </div>
            <div className="text-xs text-gray-500 mt-1">
                {netLine || (
                    <span>
                        Available: {formatCurrency(available)} · Allocated: {formatCurrency(saleAmount)}
                        {reserved > 0 && (
                            <span className="text-amber-700"> · {formatCurrency(reserved)} committed to clearing debt</span>
                        )}
                    </span>
                )}
            </div>
        </div>
    );
}

interface EntryFeeShareCgtPanelProps {
    factFind: DraftFactFind;
    asset: AssetRow;
    saleAmount: number;
    entityId?: string | null;
    cgtOverrides?: Record<string, number | null> | null;
    onCgtOverrideChange: (assetId: string, value: number | null) => void;
}

function EntryFeeShareCgtPanel({
    factFind,
    asset,
    saleAmount,
    entityId,
    cgtOverrides,
    onCgtOverrideChange,
}: EntryFeeShareCgtPanelProps) {
    if (!saleAmount || saleAmount <= 0 || !asset.costBase) return null;

    // The shared calc engine's Money fields are plain `number`; the live form state holds
    // numeric-input strings mid-edit (see frontend/src/types/formDraft.ts) — safe here because this
    // read-only estimate defensively parses (calculateSharePartialDisposalTax / parseFloat(String(x))).
    const taxSummary = calculateSharePartialDisposalTax(factFind as unknown as FactFind, asset as unknown as FactFindAsset, saleAmount, {
        trustDisposal: 'distribute',
    });

    return (
        <DisposalCgtEstimatePanel
            factFind={factFind}
            taxSummary={taxSummary}
            overrideValue={cgtOverrides?.[asset.assetId]}
            onOverrideChange={(value) => onCgtOverrideChange(asset.assetId, value)}
            entityId={entityId}
            asset={asset}
            showNetProceedsLine
            saleAmount={saleAmount}
            netProceedsLabel="Net toward entry fee (after estimated CGT)"
        />
    );
}

/** Every asset id this allocator offers a slider for — personal liquid, personal investment
 *  property, and entity-held assets. */
export function rvEntryFeeAssetIds(factFind: DraftFactFind): string[] {
    return [
        ...getPersonalLiquidAssetsForRad(factFind).map((a: AssetRow) => a.assetId),
        ...(factFind?.assets?.investmentProperty || [])
            .filter((ip) => !isEntityOwnership(ip.ownership))
            .map((ip) => `investmentProperty-${ip.id}`),
        ...getEntityEntryFeeAssetGroups(factFind).flatMap((g: { assets: AssetRow[] }) => g.assets.map((a) => a.assetId)),
    ];
}

/**
 * The entry-fee funding map ACTUALLY in force: the user's own figure for any asset they have
 * touched, the default allocation for every asset they have not.
 *
 * Shared by the allocator card and the page that feeds the engine, because they diverged: the card
 * merged per asset while the page took the user's map WHOLESALE the moment it had any key at all.
 * Dragging one slider therefore silently dropped the defaults for every other asset — from the
 * ENGINE's point of view only — so a client could see "Remaining to fund: $0" beside a Cost overview
 * claiming a $746,819 shortfall, with the projections modelling the smaller figure.
 */
export function resolveRvEntryFeeFunding(
    factFind: DraftFactFind,
    entryFeeFunding: FundingMap | null | undefined,
    defaultAllocation: FundingMap | null | undefined,
): FundingMap {
    const out: FundingMap = {};
    new Set(rvEntryFeeAssetIds(factFind)).forEach((assetId) => {
        out[assetId] = entryFeeFunding != null && assetId in entryFeeFunding
            ? (Number(entryFeeFunding[assetId]) || 0)
            : (Number(defaultAllocation?.[assetId]) || 0);
    });
    return out;
}

/**
 * Has a person actually made this allocation, or is the pre-fill still standing untouched?
 *
 * The village entry contribution MUST be paid — unlike an aged care RAD there is no
 * pay-it-daily alternative — so the tool pre-fills a default draw order and the engine funds the fee
 * whether or not anyone looks at it (see resolveRvEntryFeeFunding and the builder's fallback). That
 * makes "nobody touched this" a materially different advice position from "the adviser allocated
 * this", and the difference has to be recorded rather than inferred later. Any slider movement —
 * including setting one to zero — counts as a decision.
 */
export function isAdviserAllocatedEntryFee(entryFeeFunding: FundingMap | null | undefined): boolean {
    return Object.keys(entryFeeFunding || {}).length > 0;
}

export interface RVEntryFeeFundingProps {
    factFind: DraftFactFind;
    entryFee: number | string;
    entryFeeFunding: FundingMap;
    setEntryFeeFunding: React.Dispatch<React.SetStateAction<FundingMap>>;
    defaultAllocation?: FundingMap;
    entryFeeCgtOverrides?: Record<string, number | null>;
    setEntryFeeCgtOverrides?: React.Dispatch<React.SetStateAction<Record<string, number | null>>>;
    /** What the Debt Strategy card has already claimed of each asset, so this one cannot spend it
     *  twice — the shared-pool rule (see DebtPayoffStrategy's header). */
    reservedByAsset?: FundingMap;
}

export default function RVEntryFeeFunding({
    factFind,
    entryFee,
    entryFeeFunding,
    setEntryFeeFunding,
    defaultAllocation,
    entryFeeCgtOverrides = {},
    setEntryFeeCgtOverrides,
    reservedByAsset = {},
}: RVEntryFeeFundingProps) {
    const personalLiquidAssets = useMemo(
        () => getPersonalLiquidAssetsForRad(factFind),
        [factFind?.assets]
    );

    const personalInvestmentProperties: AssetRow[] = useMemo(() => {
        return (factFind?.assets?.investmentProperty || [])
            .filter((ip) => !isEntityOwnership(ip.ownership))
            .map((ip) => ({
                ...ip,
                typeId: 'investmentProperty',
                typeName: 'Investment property',
                assetId: `investmentProperty-${ip.id}`,
                isInvestmentProperty: true,
            }));
    }, [factFind?.assets?.investmentProperty]);

    const entityAssetGroups = useMemo(
        () => getEntityEntryFeeAssetGroups(factFind),
        [factFind?.assets, factFind?.structures]
    );

    const hasStructures = (factFind?.structures || []).length > 0;

    // The SAME resolution the page feeds the engine (see resolveRvEntryFeeFunding) — one function,
    // so what the client reads here and what gets modelled can never disagree again.
    const effectiveFunding: FundingMap = useMemo(
        () => resolveRvEntryFeeFunding(factFind, entryFeeFunding, defaultAllocation),
        [factFind, entryFeeFunding, defaultAllocation],
    );

    const fundingTotals = useMemo(
        () => computeRvEntryFeeFundingTotals(factFind as unknown as FactFind, effectiveFunding, entryFeeCgtOverrides),
        [factFind, effectiveFunding, entryFeeCgtOverrides]
    );

    const entityGrossAllocated = useMemo(
        () => sumEntityRadFunding(effectiveFunding, entityAssetGroups),
        [effectiveFunding, entityAssetGroups]
    );

    const entryFeeAmount = parseFloat(String(entryFee)) || 0;
    const remainingToFund = Math.max(0, entryFeeAmount - fundingTotals.netTotal);

    // Is the pre-fill still standing, and what is it quietly doing? Both drive the notice above the
    // sliders — the buffer it protects and the pension-before-shares preference it takes.
    const adviserAllocated = isAdviserAllocatedEntryFee(entryFeeFunding);
    const bankTotal = personalLiquidAssets
        .filter((a) => a.typeId === 'bankAccounts')
        .reduce((sum, a) => sum + (parseFloat(String(a.value)) || 0), 0);
    const bankDrawnByDefault = personalLiquidAssets
        .filter((a) => a.typeId === 'bankAccounts')
        .reduce((sum, a) => sum + (Number(effectiveFunding[a.assetId]) || 0), 0);
    const liquidAvailable = personalLiquidAssets
        .filter((a) => a.typeId !== 'investmentProperty')
        .reduce((sum, a) => sum + (parseFloat(String(a.value)) || 0), 0);
    const savingsCannotCover = entryFeeAmount > 0 && liquidAvailable < entryFeeAmount;

    /*
     * Only claim a buffer when the default actually left cash behind BECAUSE OF THE RESERVE RULE.
     *
     * When savings cannot cover the contribution there is now no default at all (the allocation is
     * left empty rather than pre-filled), so `bankTotal - bankDrawnByDefault` is the whole bank
     * balance — and the screen would explain a $700,000 "emergency buffer" that is really just money
     * nobody has allocated. A reason given for something that did not happen is worse than silence.
     */
    const reserveKeptInCash = !adviserAllocated && !savingsCannotCover
        ? Math.max(0, Math.round(bankTotal - bankDrawnByDefault))
        : 0;
    const defaultDrawsAbp = !adviserAllocated && personalLiquidAssets.some(
        (a) => a.typeId === 'accountBasedPensions' && (Number(effectiveFunding[a.assetId]) || 0) > 0,
    );
    /*
     * The explainer must describe THIS client's balance sheet, not the generic draw order
     * (owner-caught, 2026-08-26: "this client did not have any shares but it references this in the
     * commentary — it needs to be logic based otherwise consumers will get confused"). A client with
     * no shares should never read a sentence about selling shares or about the CGT that would follow.
     */
    const heldTypeIds = new Set(personalLiquidAssets.filter((a) => (parseFloat(String(a.value)) || 0) > 0).map((a) => a.typeId));
    const hasShares = heldTypeIds.has('shares');
    const DRAW_ORDER_LABELS: Array<[string, string]> = [
        ['bankAccounts', 'bank accounts'],
        ['termDeposits', 'term deposits'],
        ['accountBasedPensions', 'account-based pensions'],
        ['shares', 'shares'],
    ];
    const heldDrawOrder = DRAW_ORDER_LABELS.filter(([id]) => heldTypeIds.has(id)).map(([, label]) => label);
    /*
     * Can these savings cover the contribution AT ALL? (owner, 2026-08-26: "for this example where
     * the client has only $300k in liquid assets do you think the default starting point of selling
     * the assets is the right path when they will have to sell the home to fund it?" — no, it isn't.)
     *
     * The pre-fill still runs, because the engine has to fund the fee from something for the
     * projections to mean anything. What changes is what this box SAYS: proposing a draw order as a
     * "suggested starting point" implies a workable plan, and when the money is not there the honest
     * opening is that it is not there. Measured on liquid assets only — the property paths are a
     * different decision, made in Property strategy options.
     */

    /*
     * WHAT THIS CLIENT COULD ACTUALLY SELL, named rather than gestured at.
     *
     * The message used to point at "Property strategy options above" and compare a village
     * contribution to an aged care room ("there is no pay-part-of-it-daily option the way an aged
     * care room has"). The owner cut that: it drags aged care into a conversation the client may
     * never have opened, and it explains the village rule by reference to a scheme they have not
     * asked about. The rule stands on its own — a village contribution is paid in full.
     *
     * Naming the routes they actually HOLD is more use than naming a section: a client with an
     * investment property and no intention of leaving their home should hear about the property
     * first, not be sent scrolling.
     */
    const hasFamilyHome = (parseFloat(String((factFind as { home?: { value?: unknown } })?.home?.value ?? '')) || 0) > 0;
    const investmentProperties = ((factFind?.assets as { investmentProperty?: unknown[] } | undefined)?.investmentProperty || []) as Array<{ value?: unknown }>;
    const hasInvestmentProperty = investmentProperties.some((ip) => (parseFloat(String(ip?.value ?? '')) || 0) > 0);
    const fundingRoutes = [
        hasFamilyHome ? 'selling or renting out the family home' : null,
        hasInvestmentProperty ? 'selling an investment property' : null,
    ].filter(Boolean) as string[];

    const handleFundingChange = (assetId: string, value: string | number | undefined) => {
        const numeric = parseFloat(String(value)) || 0;
        // A zero is KEPT, not deleted. Touching a slider records an intention, and "use nothing from
        // this account" is an intention — deleting the key made the asset untouched again, so the
        // per-asset merge handed back the default allocation and the slider sprang back to it.
        setEntryFeeFunding((prev) => ({ ...prev, [assetId]: Math.max(0, numeric) }));
        if (numeric <= 0 && setEntryFeeCgtOverrides) {
            setEntryFeeCgtOverrides((prev) => {
                if (prev?.[assetId] == null) return prev;
                const next = { ...prev };
                delete next[assetId];
                return next;
            });
        }
    };

    const handleCgtOverrideChange = (assetId: string, value: number | null) => {
        if (!setEntryFeeCgtOverrides) return;
        setEntryFeeCgtOverrides((prev) => {
            const next = { ...prev };
            if (value == null) delete next[assetId];
            else next[assetId] = value;
            return next;
        });
    };

    const renderAssetRow = (asset: AssetRow, entityId: string | null = null) => {
        const saleAmount = effectiveFunding[asset.assetId] || 0;
        let netLine: React.ReactNode = null;
        if (asset.isInvestmentProperty && saleAmount > 0) {
            const available = parseFloat(String(asset.value)) || 0;
            const ipNet = computeRvIpNetForAllocation(
                factFind as unknown as FactFind,
                asset as unknown as FactFindAsset,
                Math.min(saleAmount, available)
            );
            netLine = (
                <span>
                    Available: {formatCurrency(available)} · Sale amount: {formatCurrency(saleAmount)}{' '}
                    → Estimated CGT: {formatCurrency(ipNet.cgt)} · Net to entry fee:{' '}
                    {formatCurrency(ipNet.netProceeds)}
                </span>
            );
        }

        return (
            <div key={asset.assetId} className="space-y-2">
                <EntryFeeAssetSlider
                    asset={asset}
                    saleAmount={saleAmount}
                    onFundingChange={handleFundingChange}
                    netLine={netLine}
                    reserved={Number(reservedByAsset[asset.assetId]) || 0}
                    factFind={factFind}
                />
                {asset.typeId === 'shares' && saleAmount > 0 && (
                    <EntryFeeShareCgtPanel
                        factFind={factFind}
                        asset={asset}
                        saleAmount={saleAmount}
                        entityId={entityId}
                        cgtOverrides={entryFeeCgtOverrides}
                        onCgtOverrideChange={handleCgtOverrideChange}
                    />
                )}
            </div>
        );
    };

    const hasShareLiquidation = fundingTotals.shareCgtTotal > 0;
    const hasAnyAssets =
        personalLiquidAssets.length > 0 ||
        personalInvestmentProperties.length > 0 ||
        entityAssetGroups.length > 0;

    return (
        <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Entry fee from existing assets</h3>
            <p className="text-sm text-gray-600 mb-4">
                Allocate funds from your liquid assets or investment property to pay the village entry
                fee. For share sales, estimated CGT is deducted before the entry fee amount is
                credited.
            </p>

            {/* The pre-fill is a starting point, and says so. The entry contribution has to be paid
                from something, so the tool proposes a draw order rather than leaving the projections
                unfunded — but an untouched proposal is not advice, and a client who leaves it must be
                told that plainly rather than discovering it later. */}
            {entryFeeAmount > 0 && (
                adviserAllocated ? (
                    <p className="text-sm mb-4 rounded-lg bg-white border border-slate-200/70 border-l-4 border-l-emerald-500 px-3 py-2 text-slate-700 shadow-sm">
                        <strong className="text-slate-900">You have set this allocation.</strong> These are your figures, not the tool&apos;s defaults.
                    </p>
                ) : (
                    <div className={`text-sm mb-4 rounded-lg bg-white border border-slate-200/70 border-l-4 px-3 py-2.5 text-slate-700 shadow-sm space-y-1.5 ${savingsCannotCover ? 'border-l-amber-500' : 'border-l-blue-500'}`}>
                        {savingsCannotCover ? (
                            <>
                            <p>
                                <strong className="text-slate-900">Your savings cannot cover the entry contribution.</strong>{' '}
                                They total {formatCurrency(liquidAvailable)} against {formatCurrency(entryFeeAmount)}, and a
                                village entry contribution has to be paid in full.
                                {fundingRoutes.length > 0 ? (
                                    <> That means the money has to come from {fundingRoutes.join(' or ')} — set that in{' '}
                                    <strong>Property strategy options</strong> above, and the sliders here will top it up.</>
                                ) : (
                                    <> With no property to sell or rent, this move cannot be funded as entered — a lower
                                    entry contribution is the only thing that would change that.</>
                                )}
                            </p>
                            {/*
                              * The sliders are NOT pre-filled in this state (see
                              * defaultEntryFeeAllocation): an allocation that cannot fund the move is
                              * not a partial plan, it is no plan, and handing the client one to undo
                              * was the complaint that prompted this.
                              */}
                            <p className="mt-1.5">
                                The sliders below start at zero for that reason — there is nothing to
                                allocate until the move can be funded.
                            </p>
                            </>
                        ) : (
                            <p><strong>This is a suggested starting point, not a recommendation.</strong> We have pre-filled a
                            default draw order{heldDrawOrder.length > 0 ? ` (${heldDrawOrder.join(' → ')})` : ''} so the
                            projections can run — the entry contribution has to be paid from something. Adjust any slider to
                            make it your own decision.</p>
                        )}
                        {reserveKeptInCash > 0 && (
                            <p className="mt-1.5">
                                The default deliberately leaves <strong>{formatCurrency(reserveKeptInCash)}</strong> in
                                cash as an emergency buffer — six months of living expenses, or $50,000, whichever is
                                greater — which is why your bank accounts are not drawn to zero.
                            </p>
                        )}
                        {/*
                          * Only a client who actually HOLDS shares has this trade-off to weigh.
                          *
                          * The trade-off itself used to be a card in the floating "What's happening"
                          * panel, and this paragraph pointed at it. With that panel retired the
                          * pointer would have dangled, so the reasoning moved here — which is where
                          * the decision is made anyway. It is a genuine trade-off and the default
                          * picks a side, so saying which side and why is the least the screen owes.
                          */}
                        {defaultDrawsAbp && hasShares && (
                            <p className="mt-1.5">
                                It also draws your <strong>account-based pension before your shares</strong>, to avoid
                                triggering <strong>capital gains tax</strong> on a share sale. That is not automatically
                                the better outcome: an account-based pension pays <strong>tax-free investment
                                earnings</strong> and drawing it down gives up that stream for good, while the CGT on
                                shares is a one-off cost that the 50% discount may make smaller than it looks. It is a
                                genuine trade-off, not a settled answer — move the sliders to take the other side, or run
                                the <strong>Suggestion Engine</strong>, which models both and ranks them.
                            </p>
                        )}
                        {/* One click turns a default nobody examined into a decision someone made. It
                            writes the pre-filled figures in as the adviser's own, so the saved record
                            and the projections describe the same act.
                            
                            HIDDEN when savings cannot cover the contribution: there is no default to
                            accept in that state (the allocation is left empty), so the button would
                            invite the client to confirm nothing at all. */}
                        {!savingsCannotCover && (
                        <button
                            type="button"
                            onClick={() => setEntryFeeFunding({ ...effectiveFunding })}
                            className="mt-2.5 inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                        >
                            Accept this allocation
                        </button>
                        )}
                    </div>
                )
            )}

            {!hasAnyAssets && (
                <p className="text-sm text-gray-500 italic">
                    No fundable assets available in your fact-find.
                </p>
            )}

            {/* While the pre-fill stands, the sliders LOOK provisional: a dashed amber frame, so the
                figures never read as settled just because they are populated. Adjusting one, or
                accepting them above, removes the frame. */}
            <div className={entryFeeAmount > 0 && !adviserAllocated ? 'rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-3' : ''}>
            {personalLiquidAssets.length > 0 && (
                <div className="space-y-4">
                    {hasStructures && (
                        <h4 className="text-sm font-semibold text-gray-700">Your personal assets</h4>
                    )}
                    {personalLiquidAssets.map((asset) => renderAssetRow(asset))}
                </div>
            )}

            {personalInvestmentProperties.length > 0 && (
                <div className={`space-y-4 ${personalLiquidAssets.length > 0 ? 'mt-4' : ''}`}>
                    {hasStructures && (
                        <h4 className="text-sm font-semibold text-gray-700">
                            Personal investment property
                        </h4>
                    )}
                    {personalInvestmentProperties.map((asset) => renderAssetRow(asset))}
                </div>
            )}

            {entityAssetGroups.map((group) => (
                <div key={group.entityId} className="mt-6 pt-4 border-t space-y-4">
                    <div>
                        <h4 className="text-sm font-semibold text-gray-800">{group.entityName}</h4>
                        <p className="text-xs text-gray-500">{group.structureTypeLabel}</p>
                    </div>
                    {group.assets.map((asset) => renderAssetRow(asset, group.entityId))}
                </div>
            ))}
            </div>

            {(entityGrossAllocated > 0 ||
                entityAssetGroups.some((g) =>
                    g.assets.some(
                        (a) =>
                            a.isInvestmentProperty &&
                            (effectiveFunding[a.assetId] || 0) > 0
                    )
                )) && (
                <div className="mt-4 p-3 rounded-lg bg-white border border-slate-200/70 border-l-4 border-l-amber-500 text-sm text-slate-700 shadow-sm">
                    {ENTITY_STRUCTURE_FUNDING_DISCLAIMER}
                </div>
            )}

            <div className="mt-6 pt-4 border-t space-y-2">
                {hasShareLiquidation && (
                    <div className="flex justify-between text-sm text-gray-600">
                        <span>Share liquidations (gross)</span>
                        <span>
                            {formatCurrency(
                                Object.entries(
                                    computeRadFundingTotals(
                                        factFind,
                                        Object.fromEntries(
                                            Object.entries(effectiveFunding).filter(
                                                ([k]) => !k.startsWith('investmentProperty-')
                                            )
                                        ),
                                        entryFeeCgtOverrides
                                    ).byKey
                                )
                                    .filter(([k]) => k.startsWith('shares-'))
                                    .reduce((s, [, e]) => s + e.gross, 0)
                            )}
                        </span>
                    </div>
                )}
                <div className="flex justify-between font-bold">
                    <span>Total toward entry fee (net of CGT on shares):</span>
                    <span className={fundingTotals.netTotal > entryFeeAmount ? 'text-red-500' : ''}>
                        {formatCurrency(fundingTotals.netTotal)}
                    </span>
                </div>
                <div className="flex justify-between font-bold">
                    <span>Remaining to fund:</span>
                    <span>{formatCurrency(remainingToFund)}</span>
                </div>
            </div>
        </div>
    );
}
